import {
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  of,
  switchMap,
} from 'rxjs';

import { PublicUser, User } from '@app/interfaces/auth/auth.interfaces';
import type { GlobalSearchResultItem } from '@app/interfaces/public/global-search.interfaces';
import { HeaderAnnouncement } from '@app/interfaces/public/layout.interfaces';
import { TrainingEvent } from '@app/interfaces/public/training-event.interfaces';

import { AuthService } from '@app/services/auth/auth/auth.service';
import { PublicAuthService } from '@app/services/auth/public-auth/public-auth.service';
import { ThemeService } from '@app/services/common/theme/theme.service';
import { GlobalSearchService } from '@app/services/common/global-search/global-search.service';
import { NewsService } from '@app/services/content/news/news.service';
import { TrainingEventService } from '@app/services/content/training-events/training-event.service';
import { PublicToastService } from '@app/services/ui/public-toast/public-toast.service';
import { InquiryDrawerService } from '@app/services/ui/inquiry-drawer/inquiry-drawer.service';

import { PublicAuthModalComponent } from '@app/common/components/ui/public-auth-modal/public-auth-modal.component';

@Component({
  selector: 'app-main-header',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, PublicAuthModalComponent],
  templateUrl: './main-header.component.html',
  styleUrl: './main-header.component.scss',
})
export class MainHeader implements OnInit {
  private readonly router = inject(Router);
  private readonly newsService = inject(NewsService);
  private readonly trainingEventService = inject(TrainingEventService);
  private readonly publicAuthService = inject(PublicAuthService);
  private readonly publicToastService = inject(PublicToastService);
  private readonly authService = inject(AuthService);
  private readonly inquiryDrawer = inject(InquiryDrawerService);
  private readonly globalSearchService = inject(GlobalSearchService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly searchTerms = new Subject<string>();

  isMobileMenuOpen = false;
  isDesktop = false;
  isSearchExpanded = false;
  isSolutionsDropdownOpen = false;
  isProductsDropdownOpen = false;
  isCompanyDropdownOpen = false;
  isEducationSubmenuOpen = false;
  isEducationCoursesSubmenuOpen = false;
  announcement = signal<HeaderAnnouncement | null>(null);
  happeningEvent = signal<TrainingEvent | null>(null);
  readonly announcementHeightRem = 2.85;
  readonly happeningEventHeightRem = 2.5;
  readonly headerBaseHeight = 'clamp(4rem, 4.5rem + 0.5vw, 5.625rem)';
  readonly headerBannerOffset = computed(
    () =>
      `${(this.announcement() ? this.announcementHeightRem : 0) + (this.happeningEvent() ? this.happeningEventHeightRem : 0)}rem`,
  );
  readonly happeningEventTop = computed(
    () => `${this.announcement() ? this.announcementHeightRem : 0}rem`,
  );
  searchQuery = '';
  searchResults: GlobalSearchResultItem[] = [];
  searchLoading = false;
  showSearchResults = false;
  showUserMenu = false;
  showProfileModal = false;
  profileEditing = false;
  profileSaving = false;
  profileError = '';
  profileForm = {
    name: '',
    email: '',
    phone: '',
    organization: '',
    position: '',
  };
  showLogoutModal = false;
  showPublicAuthModal = false;
  resetPasswordEmail = '';
  resetPasswordToken = '';

  public themeService = inject(ThemeService);
  readonly activePublicUser = this.publicAuthService.effectiveUser;
  readonly profileAccount = this.authService.user;

  ngOnInit(): void {
    this.checkScreenSize();
    this.syncPublicHeaderOffset();
    this.loadAnnouncement();
    this.loadHappeningEvent();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.openPasswordResetFromUrl();
        this.closeAllDesktopDropdowns();
      });
    this.openPasswordResetFromUrl();

    this.searchTerms
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((query) => {
          if (!query) {
            return of({ data: [] });
          }

          this.searchLoading = true;
          return this.globalSearchService.search(query, 8).pipe(catchError(() => of({ data: [] })));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.searchResults = response.data ?? [];
        this.searchLoading = false;
      });
  }

  toggleMobileMenu(): void {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;

    if (this.isMobileMenuOpen) {
      // if mobile menu opens, close search to avoid clutter
      this.isSearchExpanded = false;
    }
    this.updateBodyScrollLock();
  }

  toggleSearch(): void {
    this.isSearchExpanded = !this.isSearchExpanded;

    if (!this.isSearchExpanded) {
      this.showSearchResults = false;
    }

    // if search opens, close mobile menu
    if (this.isSearchExpanded && this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
    this.updateBodyScrollLock();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScreenSize();
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.isDesktop) {
      this.closeAllDesktopDropdowns();
    }
  }

  @HostListener('window:wheel')
  onWindowWheel(): void {
    if (this.isDesktop) {
      this.closeAllDesktopDropdowns();
    }
  }

  @HostListener('window:touchmove')
  onWindowTouchMove(): void {
    if (this.isDesktop) {
      this.closeAllDesktopDropdowns();
    }
  }

  private checkScreenSize(): void {
    const wasDesktop = this.isDesktop;
    // content-based breakpoint matching the SCSS variable $nav-switch-point
    this.isDesktop = window.matchMedia('(min-width: 60rem)').matches;

    // close mobile menu if switching to desktop view
    if (this.isDesktop && wasDesktop === false && this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  onNavLinkClick(): void {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    if (!this.isDesktop && this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
    this.isSolutionsDropdownOpen = false;
    this.isProductsDropdownOpen = false;
    this.isCompanyDropdownOpen = false;
    this.closeEducationSubmenus();
  }

  onSearchSubmit(rawValue?: string): void {
    const query = (rawValue ?? this.searchQuery ?? '').trim();

    this.router.navigate(['/search'], {
      queryParams: {
        q: query.length > 0 ? query : null,
      },
      queryParamsHandling: 'merge',
    });

    if (!this.isDesktop) {
      this.isSearchExpanded = false;
    }

    this.showSearchResults = false;
  }

  onSearchInput(value: string): void {
    const query = value.trim();
    this.showSearchResults = query.length > 0;
    this.searchLoading = query.length > 0;

    if (!query) {
      this.searchResults = [];
      this.searchLoading = false;
    }

    this.searchTerms.next(query);
  }

  onSearchFocus(): void {
    this.showSearchResults = this.searchQuery.trim().length > 0;
  }

  onSearchBlur(): void {
    setTimeout(() => {
      this.showSearchResults = false;
    }, 150);
  }

  selectSearchResult(): void {
    this.searchQuery = '';
    this.searchResults = [];
    this.showSearchResults = false;

    if (!this.isDesktop) {
      this.isSearchExpanded = false;
    }
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchResults = [];
    this.searchLoading = false;
    this.showSearchResults = false;
    this.searchTerms.next('');
  }

  onHomeClick(event: Event): void {
    // If already on home, still treat click as "go home again" by scrolling to top.
    if (this.router.url === '/' || this.router.url.startsWith('/#')) {
      event.preventDefault();
      this.onNavLinkClick();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    this.onNavLinkClick();
  }

  onSolutionsTriggerClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isSolutionsDropdownOpen = !this.isSolutionsDropdownOpen;
    if (this.isSolutionsDropdownOpen) {
      this.isProductsDropdownOpen = false;
      this.isCompanyDropdownOpen = false;
    }
    if (!this.isSolutionsDropdownOpen || !this.isDesktop) {
      this.closeEducationSubmenus();
    }
  }

  onSolutionsMouseEnter(): void {
    if (!this.isDesktop) return;
    this.isSolutionsDropdownOpen = true;
    this.isProductsDropdownOpen = false;
    this.isCompanyDropdownOpen = false;
  }

  onSolutionsMouseLeave(): void {
    if (!this.isDesktop) return;
    this.isSolutionsDropdownOpen = false;
    this.closeEducationSubmenus();
  }

  onProductsTriggerClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isProductsDropdownOpen = !this.isProductsDropdownOpen;
    if (this.isProductsDropdownOpen) {
      this.isSolutionsDropdownOpen = false;
      this.isCompanyDropdownOpen = false;
    }
  }

  onProductsMouseEnter(): void {
    if (!this.isDesktop) return;
    this.isProductsDropdownOpen = true;
    this.isSolutionsDropdownOpen = false;
    this.isCompanyDropdownOpen = false;
  }

  onProductsMouseLeave(): void {
    if (!this.isDesktop) return;
    this.isProductsDropdownOpen = false;
  }

  onCompanyTriggerClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isCompanyDropdownOpen = !this.isCompanyDropdownOpen;
    if (this.isCompanyDropdownOpen) {
      this.isSolutionsDropdownOpen = false;
      this.isProductsDropdownOpen = false;
    }
  }

  onCompanyMouseEnter(): void {
    if (!this.isDesktop) return;
    this.isCompanyDropdownOpen = true;
    this.isSolutionsDropdownOpen = false;
    this.isProductsDropdownOpen = false;
  }

  onCompanyMouseLeave(): void {
    if (!this.isDesktop) return;
    this.isCompanyDropdownOpen = false;
  }

  onComingSoonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.showLockedFeatureNotice();
  }

  isSolutionsActive(): boolean {
    return (
      this.router.url.startsWith('/solutions') ||
      this.router.url.startsWith('/product-catalogs') ||
      this.router.url.startsWith('/product-categories') ||
      this.router.url.startsWith('/higher-education/') ||
      this.router.url.startsWith('/product/')
    );
  }

  isCompanyActive(): boolean {
    return (
      this.router.url.startsWith('/about-us') ||
      (this.router.url.startsWith('/company/') && !this.router.url.startsWith('/company/careers'))
    );
  }

  isProductsActive(): boolean {
    return this.router.url.startsWith('/partners/');
  }

  isVisitorSignupActive(): boolean {
    return this.router.url.startsWith('/visitor-signup');
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.isDesktop && this.isSolutionsDropdownOpen) {
      this.isSolutionsDropdownOpen = false;
      this.closeEducationSubmenus();
    }
    if (this.isDesktop && this.isProductsDropdownOpen) {
      this.isProductsDropdownOpen = false;
    }
    if (this.isDesktop && this.isCompanyDropdownOpen) {
      this.isCompanyDropdownOpen = false;
    }
    this.showUserMenu = false;
  }

  onEducationSubmenuToggle(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isEducationSubmenuOpen) {
      this.closeEducationSubmenus();
      return;
    }
    this.isEducationSubmenuOpen = true;
  }

  onEducationPrimaryClick(event: Event): void {
    if (this.isDesktop) {
      this.onNavLinkClick();
      return;
    }

    this.onEducationSubmenuToggle(event);
  }

  onEducationCoursesToggle(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isEducationCoursesSubmenuOpen = !this.isEducationCoursesSubmenuOpen;
    this.isEducationSubmenuOpen = true;
  }

  private dispatchPublicToast(detail: {
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
    durationMs?: number;
  }): void {
    this.publicToastService.show(detail);
  }

  private closeEducationSubmenus(): void {
    this.isEducationSubmenuOpen = false;
    this.isEducationCoursesSubmenuOpen = false;
  }

  private closeAllDesktopDropdowns(): void {
    this.isSolutionsDropdownOpen = false;
    this.isProductsDropdownOpen = false;
    this.isCompanyDropdownOpen = false;
    this.closeEducationSubmenus();
  }

  private loadAnnouncement(): void {
    this.newsService.getLatestAnnouncement().subscribe({
      next: (response: any) => {
        const item = response?.data?.[0] ?? null;
        this.announcement.set(item);
        this.syncPublicHeaderOffset();
      },
      error: () => {
        this.announcement.set(null);
        this.syncPublicHeaderOffset();
      },
    });
  }

  private loadHappeningEvent(): void {
    this.trainingEventService.getTrainingEvents().subscribe({
      next: (response) => {
        const combined = [...(response?.upcoming || []), ...(response?.past || [])];
        const match = combined.find((event) => this.isHappeningToday(event));
        this.happeningEvent.set(match ?? null);
        this.syncPublicHeaderOffset();
      },
      error: () => {
        this.happeningEvent.set(null);
        this.syncPublicHeaderOffset();
      },
    });
  }

  private syncPublicHeaderOffset(): void {
    this.document.documentElement.style.setProperty(
      '--public-header-height',
      `calc(${this.headerBaseHeight} + 0.5rem)`,
    );
    this.document.documentElement.style.setProperty(
      '--public-banner-offset',
      this.headerBannerOffset(),
    );
    this.document.documentElement.style.setProperty(
      '--public-header-offset',
      `calc(${this.headerBaseHeight} + ${this.headerBannerOffset()} + 0.5rem)`,
    );
  }

  private isHappeningToday(event: TrainingEvent): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = this.parseDateOnly(event.date);
    if (!startDate) {
      return false;
    }
    const endDate = this.parseDateOnly(event.end_date) ?? startDate;
    return startDate <= today && endDate >= today;
  }

  private parseDateOnly(dateValue: string | null | undefined): Date | null {
    if (!dateValue) {
      return null;
    }
    const date = new Date(`${dateValue.substring(0, 10)}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private updateBodyScrollLock(): void {
    document.body.style.overflow = this.isMobileMenuOpen ? 'hidden' : '';
  }

  private showLockedFeatureNotice(): void {
    this.dispatchPublicToast({
      title: 'Coming Soon',
      message: 'Not available yet. Please check back soon.',
      type: 'info',
      durationMs: 2800,
    });
  }

  toggleUserMenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.showUserMenu = !this.showUserMenu;
  }

  openProfile(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.showUserMenu = false;
    this.setProfileForm(this.authService.user());
    this.profileEditing = false;
    this.showProfileModal = true;
  }

  closeProfile(): void {
    this.showProfileModal = false;
    this.profileEditing = false;
    this.profileError = '';
  }

  editProfile(): void {
    this.setProfileForm(this.authService.user());
    this.profileError = '';
    this.profileEditing = true;
  }

  cancelProfileEdit(): void {
    this.profileEditing = false;
    this.profileError = '';
  }

  saveProfile(): void {
    const name = this.profileForm.name.trim();
    const email = this.profileForm.email.trim();
    if (!name || !email) {
      this.profileError = 'Name and email are required.';
      return;
    }

    this.profileSaving = true;
    this.profileError = '';
    this.authService
      .updateProfile({
        name,
        email,
        phone: this.profileForm.phone.trim() || null,
        organization: this.profileForm.organization.trim() || null,
        position: this.profileForm.position.trim() || null,
      })
      .subscribe({
        next: (response) => {
          this.authService.saveUpdatedUser(response.user);
          this.profileSaving = false;
          this.closeProfile();
          this.dispatchPublicToast({
            title: 'Profile Updated',
            message: 'Your account details were updated successfully.',
            type: 'success',
          });
        },
        error: (error) => {
          this.profileSaving = false;
          this.profileError = error?.error?.message || 'Unable to update your profile.';
        },
      });
  }

  private setProfileForm(user: User | null): void {
    this.profileForm = {
      name: user?.name ?? '',
      email: user?.email ?? '',
      phone: user?.phone ?? '',
      organization: user?.organization ?? '',
      position: user?.position ?? '',
    };
  }

  openPublicAuthModal(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.showPublicAuthModal = true;
    this.showUserMenu = false;

    if (!this.isDesktop && this.isMobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  closePublicAuthModal(): void {
    this.showPublicAuthModal = false;
    this.resetPasswordEmail = '';
    this.resetPasswordToken = '';
  }

  private openPasswordResetFromUrl(): void {
    const tree = this.router.parseUrl(this.router.url);
    const resetPassword = String(tree.queryParams['resetPassword'] ?? '') === '1';
    const email = String(tree.queryParams['email'] ?? '');
    const token = String(tree.queryParams['token'] ?? '');

    if (!resetPassword || !email || !token) {
      return;
    }

    this.resetPasswordEmail = email;
    this.resetPasswordToken = token;
    this.showPublicAuthModal = true;

    delete tree.queryParams['resetPassword'];
    delete tree.queryParams['email'];
    delete tree.queryParams['token'];
    void this.router.navigateByUrl(tree, { replaceUrl: true });
  }

  openInquiryDrawer(): void {
    this.onNavLinkClick();
    this.inquiryDrawer.open();
  }

  onLogout(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.showUserMenu = false;
    this.showLogoutModal = true;
  }

  closeLogoutModal(): void {
    this.showLogoutModal = false;
  }

  confirmLogout(): void {
    this.showLogoutModal = false;
    this.publicAuthService.logout();
    this.dispatchPublicToast({
      title: 'Logged Out',
      message: 'Your visitor session has been closed.',
      type: 'success',
    });
  }

  async onSetPassword(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.showUserMenu = false;
    this.router.navigate(['/visitor-signup'], { queryParams: { setPassword: 1 } });
  }

  getUserBadgeSecondary(user: PublicUser): string {
    if (user.accountStatus === 'admin') {
      return user.visitPurpose || 'Admin CMS';
    }

    const purpose = String(user.visitPurpose ?? '').trim();
    if (!purpose) {
      return '';
    }

    return purpose;
  }

  getUserGreeting(user: PublicUser): string {
    const fullName = String(user.fullName ?? '').trim();
    if (!fullName) {
      return 'Welcome';
    }

    const firstName = fullName.split(/\s+/)[0] || fullName;
    return `Hi, ${firstName}`;
  }

  canAccessAdminCms(): boolean {
    return this.authService.isAdmin;
  }
}
